/**
 * Copyright (c) 2018-present, Tomaruba, Inc.
 * All rights reserved.
 *
 */
import './SmtpSettingView.scss'
import React from 'react'
import _ from 'lodash'
import r from '../../../util/Renderer'
import { DeleteButton, Dropdown, ListItem, TextInput } from '../../atoms'
import { Edit, ListGroup } from '../../molecules'
import Cell from '../../templates/Cell'
import LocalArea from '../../templates/LocalArea'
import SmtpSettingActions from '../../../action/auto_action/SmtpSettingActions'
import { NavigationDrawer, NormalButton } from 'ui-library/dist/external/components'
import OptionState from '../../../store/options/state/OptionState'
import { Record } from 'immutable'

function SmtpSettingView(props) {
  const id = props.match.params.id || null
  const showEdit = _.propertyOf(props)('showEdit') || false
  const toolbar = { label: 'メール設定', items: props.accountMenu }

  return (
    <div id="SmtpSetting">
      <NavigationDrawer toolbar={toolbar} navigations={props.navigations} />

      <LocalArea>
        <div className="LocalArea--right">
          <NormalButton onClick={() => SmtpSettingActions.onCreate()}>新規作成</NormalButton>
        </div>
      </LocalArea>

      <div className="SmtpSetting__List">
        <ListGroup border="underline" className="ItemGroup">
          {props.smtp_settings.map(setting => new SmtpSettingListItem(setting))}
        </ListGroup>

        {r.renderIf(showEdit)(
          // 設定されている値を View に渡す
          <SmtpSettingEditView id={id} smtp_setting={props.smtp_setting} />
        )}
      </div>
    </div>
  )
}

function SmtpSettingListItem(smtp_setting) {
  return (
    <ListItem key={smtp_setting.id} className="Item">
      <div className="col-12">
        <div className="row">
          <div className="col-8 col-lg-8 align-self-center">
            <Cell className="Item__Address">
              <span>{smtp_setting.user_name}</span>
            </Cell>
          </div>
          <div className="ml-auto col-auto align-self-center">
            <Cell className="Item__Action">
              <NormalButton onClick={() => SmtpSettingActions.onUpdate(smtp_setting)}>編集</NormalButton>
            </Cell>
          </div>
        </div>
      </div>
    </ListItem>
  )
}

function SmtpSettingEditView(props) {
  const smtp_setting = props.smtp_setting
  return (
    <Edit
      title={props.id ? `SMTP設定ID：${props.id}` : 'SMTP設定の作成'}
      buttonLabel={props.id ? '更新' : '作成'}
      onSubmit={props.id ? () => SmtpSettingActions.onRequestUpdate(smtp_setting) : () => SmtpSettingActions.onRequestCreate(smtp_setting)}
      onCancel={SmtpSettingActions.onCancel}
    >
      <div className="EditArea__row">
        <div className="EditArea__row--full">
          <div className="EditArea__column">
            <TextInput value={smtp_setting.domain} type="text" name="domain" label={'ドメイン名'} placeholder="ドメイン名" option="required" onChange={SmtpSettingActions.onEdit} />
          </div>
        </div>
      </div>

      <div className="EditArea__row">
        <div className="EditArea__row--full">
          <div className="EditArea__column">
            <TextInput value={smtp_setting.address} type="text" name="address" label={'SMTPアドレス'} placeholder="SMTPアドレス" option="required" onChange={SmtpSettingActions.onEdit} />
          </div>
        </div>
      </div>

      <div className="EditArea__row">
        <div className="EditArea__row--full">
          <div className="EditArea__column">
            <TextInput value={smtp_setting.password} type="password" name="password" label={'SMTPパスワード'} placeholder="SMTPパスワード" option="required" onChange={SmtpSettingActions.onEdit} />
          </div>
        </div>
      </div>

      <div className="EditArea__row">
        <div className="EditArea__row--full">
          <div className="EditArea__column">
            <TextInput value={smtp_setting.user_name} type="text" name="user_name" label={'SMTP email'} placeholder="SMTP email" option="required" onChange={SmtpSettingActions.onEdit} />
          </div>
        </div>
      </div>

      <div className="EditArea__row">
        <div className="EditArea__row--half">
          <div className="EditArea__column">
            <p>ポート番号</p>
            <Dropdown name="port" options={getPorts()} value={smtp_setting.port || 587} onChange={SmtpSettingActions.onEdit} placeholder="ポート番号" />
          </div>
        </div>
        <div className="EditArea__row--half">
          <div className="EditArea__column">
            <p>認証方式</p>
            <Dropdown name="authentication" options={getAuthentications()} value={smtp_setting.authentication || 'plain'} onChange={SmtpSettingActions.onEdit} placeholder="認証形式" />
          </div>
        </div>
      </div>
      <div className="EditArea__row">
        <div className="EditArea__row--half">
          <div className="EditArea__column">
            <p>OpenSSL証明書</p>
            <Dropdown
              name="openssl_verify_mode"
              options={getOpensslVerifyMode()}
              value={smtp_setting.openssl_verify_mode || 'none'}
              onChange={SmtpSettingActions.onEdit}
              placeholder="OpenSSL認証方式"
            />
          </div>
        </div>
        <div className="EditArea__row--half">
          <div className="EditArea__column">
            <p>STARTTLS設定</p>
            <Dropdown
              name="enable_starttls_auto"
              options={getEnableStartTlsAuto()}
              value={smtp_setting.enable_starttls_auto === true ? true : false}
              onChange={SmtpSettingActions.onEdit}
              placeholder="STARTTLS"
            />
          </div>
        </div>
      </div>
      {r.renderIf(props.id)(
        <div className="EditArea__row">
          <div className="EditArea__row--full">
            <div className="EditArea__column">
              <DeleteButton onClick={() => SmtpSettingActions.onRequestDelete(props.id)}>削除</DeleteButton>
            </div>
          </div>
        </div>
      )}
    </Edit>
  )
}

function getPorts() {
  return [25, 465, 587].map(v => {
    return Record(OptionState)().merge({ label: v, value: v })
  })
}

function getAuthentications() {
  const plain = { label: 'プレーン認証', value: 'plain' }
  const login = { label: 'ログイン認証', value: 'login' }
  const cram_md5 = { label: 'CRAM-MD5認証', value: 'cram_md5' }
  return [plain, login, cram_md5].map(state => {
    return Record(OptionState)().merge({ label: state.label, value: state.value })
  })
}

function getOpensslVerifyMode() {
  const none = { label: 'SSL認証なし', value: 'none' }
  const peer = { label: 'PEER認証', value: 'peer' }
  const client_once = { label: 'クライアントで1度だけ認証', value: 'client_once' }
  const fail_if_no_peer_cert = { label: 'PEER証明がないとき失敗', value: 'fail_if_no_peer_cert' }
  return [none, peer, client_once, fail_if_no_peer_cert].map(state => {
    return Record(OptionState)().merge({ label: state.label, value: state.value })
  })
}

function getEnableStartTlsAuto() {
  return [true, false].map(v => {
    return Record(OptionState)().merge({ label: v === true ? '設定する' : '設定しない', value: v })
  })
}

export default SmtpSettingView
